import { defineStore } from "pinia";
import {
  type userType,
  store,
  router,
  resetRouter,
  routerArrays,
  storageLocal
} from "../utils";
import {
  type UserResult,
  type RefreshTokenResult,
  getLogin,
  refreshTokenApi
} from "@/api/user";
import { useMultiTagsStoreHook } from "./multiTags";
import { closeAllDialog } from "@/components/ReDialog";
import { type DataInfo, setToken, removeToken, userKey } from "@/utils/auth";

export const useUserStore = defineStore({
  id: "pure-user",
  state: (): userType => ({
    // 用户id
    id: storageLocal().getItem<DataInfo<number>>(userKey)?.id ?? null,
    // 头像
    avatar: storageLocal().getItem<DataInfo<number>>(userKey)?.avatar ?? "",
    // 用户账号
    userCode: storageLocal().getItem<DataInfo<number>>(userKey)?.userCode ?? "",
    // 用户名
    userName: storageLocal().getItem<DataInfo<number>>(userKey)?.userName ?? "",
    // 公司名称
    companyName:
      storageLocal().getItem<DataInfo<number>>(userKey)?.companyName ?? "",
    // 公司id
    companyId:
      storageLocal().getItem<DataInfo<number>>(userKey)?.companyId ?? "",
    // 账号类型
    accountType:
      storageLocal().getItem<DataInfo<number>>(userKey)?.accountType ?? "",
    // 页面级别权限
    roles: storageLocal().getItem<DataInfo<number>>(userKey)?.roles ?? [],
    // 按钮级别权限
    permissions:
      storageLocal().getItem<DataInfo<number>>(userKey)?.permissions ?? [],
    // 是否勾选了登录页的免登录
    isRemembered: false,
    // 登录页的免登录存储几天，默认7天
    loginDay: 7
  }),
  actions: {
    /** 存储用户ID */
    SET_USERID(id: string) {
      this.id = id;
    },
    /** 存储用户头像 */
    SET_AVATAR(avatar: string) {
      this.avatar = avatar;
    },
    /** 存储用户账号 */
    SET_USERCODE(userCode: string) {
      this.userCode = userCode;
    },
    /** 存储用户名 */
    SET_USERNAME(userName: string) {
      this.userName = userName;
    },
    /** 存储公司名称 */
    SET_COMPANYNAME(companyName: string) {
      this.companyName = companyName;
    },
    /** 存储公司ID */
    SET_COMPANYID(companyId: string) {
      this.companyId = companyId;
    },
    /** 存储用户类型 */
    SET_ACCOUNTTYPE(accountType: string) {
      this.accountType = accountType;
    },
    /** 存储角色 */
    SET_ROLES(roles: Array<string>) {
      this.roles = roles;
    },
    /** 存储按钮级别权限 */
    SET_PERMS(permissions: Array<string>) {
      this.permissions = permissions;
    },
    /** 存储是否勾选了登录页的免登录 */
    SET_ISREMEMBERED(bool: boolean) {
      this.isRemembered = bool;
    },
    /** 设置登录页的免登录存储几天 */
    SET_LOGINDAY(value: number) {
      this.loginDay = Number(value);
    },
    /** 登入 */
    async loginByUsername(data) {
      return new Promise<UserResult>((resolve, reject) => {
        getLogin(data)
          .then(data => {
            if (data.code === 200) {
              data.data.refreshToken = data.data.accessToken;
              setToken(data.data);
            }
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /** 前端登出（不调用接口） */
    logOut() {
      this.username = "";
      this.roles = [];
      this.permissions = [];
      removeToken();
      closeAllDialog();
      useMultiTagsStoreHook().handleTags("equal", [...routerArrays]);
      resetRouter();
      router.push("/login");
      // 临时解决某些页面“全屏”问题
      location.reload();
    },
    /** 刷新`token` */
    async handRefreshToken(data) {
      return new Promise<RefreshTokenResult>((resolve, reject) => {
        refreshTokenApi(data)
          .then(data => {
            if (data) {
              setToken(data.data);
              resolve(data);
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    }
  }
});

export function useUserStoreHook() {
  return useUserStore(store);
}
